import { Button } from '@codegouvfr/react-dsfr/Button';
import Tooltip from '@codegouvfr/react-dsfr/Tooltip';
import { USER_ROLE_TEXTS } from '@common/config';
import { useSessionContext } from '@common/hooks';
import {
  ADMINS_ROLES,
  Sorter,
  useDeleteOrganismeUtilisateurSimpleMutation,
  UtilisateurDto,
  UtilisateurRole,
} from '@common/services';
import { ConfirmationModal, FcTable, FcTableColumnConfig, getModalById } from '@common/ui';
import { EditOfaUtilisateurModal } from '@common/ui/modal/organisme';
import { DateUtils } from '@common/utils';
import React, { useEffect, useRef, useState } from 'react';
import { Panel } from '../base/Panel';

const deleteUtilisateurConfirmationModal = getModalById('utilisateur-delete-confirmation-modal');
const editUserInfoModal = getModalById('edit-user-info-modal');

export const OfaUsersPanel = ({ utilisateurs, organismeId, isFc }: OfaUsersPanelProps) => {
  const [sortedUtilisateurs, setSortedUtilisateurs] = useState(utilisateurs);
  const [sorter, setSorter] = useState<Sorter | null>({ field: 'role', direction: 'asc' });
  const [isAddMode, setIsAddMode] = useState<boolean>(false);
  const [confirmationDeleteModalDescription, setConfirmationDeleteModalDescription] = useState<string>('');
  const [utilisateurToEdit, setUtilisateurToEdit] = useState<UtilisateurDto>();

  const utilisateurToDeleteRef = useRef<UtilisateurDto | null>(null);

  const { role: sessionRole } = useSessionContext();

  const [deleteOrganismeUtilisateur] = useDeleteOrganismeUtilisateurSimpleMutation();

  useEffect(() => {
    const sorted = utilisateurs.toSorted((a, b) => {
      if (!sorter) return 0;

      const aVal = a[sorter.field];
      const bVal = b[sorter.field];

      // Gestion des valeurs nulles/undefined
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Comparaison pour les chaînes de caractères (insensible à la casse)
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const comparison = aVal.localeCompare(bVal, 'fr');
        return sorter.direction === 'asc' ? comparison : -comparison;
      }

      // Comparaison pour les nombres et autres types
      if (aVal === bVal) return 0;
      const comparison = aVal > bVal ? 1 : -1;
      return sorter.direction === 'asc' ? comparison : -comparison;
    });

    setSortedUtilisateurs(sorted);
  }, [utilisateurs, sorter]);

  const handleEdit = (utilisateur: UtilisateurDto) => {
    setUtilisateurToEdit(utilisateur);
    setIsAddMode(false);
    editUserInfoModal.open();
  };

  const handleDelete = (utilisateur: UtilisateurDto) => {
    setConfirmationDeleteModalDescription(`Vous êtes sur le point de supprimer l'utilisateur ${utilisateur.nom} ${utilisateur.prenom}.`);
    utilisateurToDeleteRef.current = utilisateur;
    deleteUtilisateurConfirmationModal.open();
  };

  const handleConfirmationDeletion = async () => {
    await deleteOrganismeUtilisateur({
      id: organismeId,
      utilisateurId: utilisateurToDeleteRef.current?.id,
      utilisateurRole: utilisateurToDeleteRef.current?.role,
    }).unwrap();
    deleteUtilisateurConfirmationModal.close();
  };

  const handleAdd = () => {
    setUtilisateurToEdit(null);
    setIsAddMode(true);
    editUserInfoModal.open();
  };

  const isAdmin = isFc || ADMINS_ROLES.includes(sessionRole);

  const columnsConfig: FcTableColumnConfig<UtilisateurDto>[] = [
    {
      header: { title: 'Rôle', sorting: true },
      field: 'role',
      render: (role) => USER_ROLE_TEXTS[role],
    },
    {
      header: { title: 'Prénom', sorting: true },
      field: 'prenom',
    },
    {
      header: { title: 'Nom', sorting: true },
      field: 'nom',
    },
    {
      header: { title: 'Dernière connexion', sorting: true },
      field: 'dateConnexion',
      render: (date) => (date) ? DateUtils.formatDateConnexion(date) : 'Inconnue',
    },
  ];

  if (isAdmin) columnsConfig.push({
    header: { title: '' },
    field: 'id',
    maxWidth: '150px',
    displayOnHover: true,
    render: (id, utilisateur) => {
      const isRoleSimple = (utilisateur.role === UtilisateurRole.SIMPLE);
      const tooltipDisplayStyle = (!isRoleSimple) ? 'unset' : 'none';
      return (
        <div className="flex gap-2">
          <Tooltip title="Uniquement les utilisateurs simple peuvent être modifiés"
                   kind="hover"
                   style={{ display: tooltipDisplayStyle }}>

            <Button iconId="fr-icon-edit-fill"
                    className={'color-primary'}
                    priority="tertiary no outline"
                    onClick={() => handleEdit(utilisateur)}
                    title={(isRoleSimple) ? 'Modifier l\'utilisateur' : ''}
                    disabled={!isRoleSimple} />
          </Tooltip>
          <Tooltip title="Uniquement les utilisateurs simple peuvent être supprimés"
                   kind="hover"
                   style={{ display: tooltipDisplayStyle }}>

            <Button iconId="fr-icon-delete-fill"
                    className={'color-primary'}
                    disabled={!isRoleSimple}
                    priority="tertiary no outline"
                    onClick={() => handleDelete(utilisateur)}
                    title={(isRoleSimple) ? 'Supprimer l\'utilisateur' : ''} />
          </Tooltip>
        </div>
      );
    },
  });

  const utilisateursSimpleSize = utilisateurs.filter(u => u.role === UtilisateurRole.SIMPLE).length;

  return (
    <Panel title="Utilisateurs"
           cornerButton={(isAdmin) ? {
             iconId: 'fr-icon-add-line',
             rotate90: true,
             onClick: handleAdd,
             disabled: (utilisateursSimpleSize >= 2),
             toolTipMessage: (utilisateursSimpleSize >= 2) ? 'Vous avez atteint le nombre maximum d\'utilisateurs' : undefined,
           } : undefined}>

      <FcTable<UtilisateurDto> data={sortedUtilisateurs}
                               sorter={sorter}
                               onSorterChange={setSorter}
                               columnsConfig={columnsConfig} />

      <ConfirmationModal dsfrModal={deleteUtilisateurConfirmationModal}
                         description={confirmationDeleteModalDescription}
                         subDescription="Êtes-vous certain(e) de vouloir continuer ?"
                         confirmProps={{ onClick: () => handleConfirmationDeletion() }} />

      <EditOfaUtilisateurModal organismeId={organismeId}
                               dsfrModal={editUserInfoModal}
                               isAddMode={isAddMode}
                               utilisateur={utilisateurToEdit} />
    </Panel>
  );
};

interface OfaUsersPanelProps {
  utilisateurs: UtilisateurDto[];
  organismeId: number;
  isFc: boolean; // boolean pour dire si on se trouve coté fc ou non
}